<script setup lang="ts">
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const trigger = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const tooltipContent = ref<HTMLElement>()
const visible = ref(false)
const pinned = ref(false)
const ready = ref(false)
const dismissedUntilLeave = ref(false)
const triggerHovered = ref(false)
const tooltipHovered = ref(false)
const tooltipId = `enterprise-record-retention-${useId().replaceAll(':', '')}`
const position = ref({
  top: 0,
  left: 0,
  arrowLeft: 24,
  maxHeight: 480,
  placement: 'below' as 'above' | 'below',
})
let hoverTimer: ReturnType<typeof setTimeout> | undefined
let closeTimer: ReturnType<typeof setTimeout> | undefined

const rtl = computed(() => String(locale.value).toLowerCase().startsWith('ar'))

function clearTimers() {
  clearTimeout(hoverTimer)
  clearTimeout(closeTimer)
}

async function updatePosition() {
  if (!visible.value || !trigger.value) return
  ready.value = false
  await nextTick()
  if (!tooltip.value || !tooltipContent.value) return

  const triggerRect = trigger.value.getBoundingClientRect()
  const width = tooltip.value.offsetWidth || 400
  const viewportPadding = 12
  const gap = 8
  const anchorCenter = triggerRect.left + triggerRect.width / 2
  const left = Math.min(
    Math.max(anchorCenter - width / 2, viewportPadding),
    Math.max(viewportPadding, window.innerWidth - viewportPadding - width),
  )
  const fullHeight = tooltipContent.value.scrollHeight
  const availableBelow = Math.max(0, window.innerHeight - triggerRect.bottom - gap - viewportPadding - 2)
  const availableAbove = Math.max(0, triggerRect.top - gap - viewportPadding - 2)
  const placement = availableBelow < Math.min(fullHeight, 320) && availableAbove > availableBelow ? 'above' : 'below'
  const availableHeight = placement === 'above' ? availableAbove : availableBelow
  const maxHeight = Math.max(1, Math.floor(availableHeight))
  const renderedHeight = Math.min(fullHeight, maxHeight)

  position.value = {
    top: placement === 'above'
      ? Math.max(viewportPadding, triggerRect.top - gap - renderedHeight - 2)
      : triggerRect.bottom + gap,
    left,
    arrowLeft: Math.min(Math.max(anchorCenter - left - 7, 18), width - 32),
    maxHeight,
    placement,
  }
  ready.value = true
}

function open() {
  visible.value = true
  void updatePosition()
}

function close() {
  visible.value = false
  pinned.value = false
  ready.value = false
}

function scheduleOpen() {
  clearTimeout(closeTimer)
  if (dismissedUntilLeave.value || pinned.value) return
  clearTimeout(hoverTimer)
  hoverTimer = setTimeout(open, 300)
}

function scheduleClose() {
  clearTimeout(hoverTimer)
  if (pinned.value) return
  clearTimeout(closeTimer)
  closeTimer = setTimeout(() => {
    if (!triggerHovered.value && !tooltipHovered.value) close()
  }, 100)
}

function onTriggerEnter() {
  triggerHovered.value = true
  scheduleOpen()
}

function onTriggerLeave() {
  triggerHovered.value = false
  dismissedUntilLeave.value = false
  scheduleClose()
}

function onTooltipEnter() {
  tooltipHovered.value = true
  clearTimeout(closeTimer)
}

function onTooltipLeave() {
  tooltipHovered.value = false
  scheduleClose()
}

function togglePinned() {
  clearTimers()
  if (pinned.value) {
    dismissedUntilLeave.value = true
    close()
    return
  }
  dismissedUntilLeave.value = false
  pinned.value = true
  open()
}

function onDocumentPointerDown(event: PointerEvent) {
  const target = event.target as Node
  if (!visible.value || trigger.value?.contains(target) || tooltip.value?.contains(target)) return
  close()
}

function onDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !visible.value) return
  close()
  trigger.value?.focus()
}

function onViewportScroll(event: Event) {
  if (event.target instanceof Node && tooltip.value?.contains(event.target)) return
  void updatePosition()
}

watch(locale, () => {
  if (visible.value) void updatePosition()
}, { flush: 'post' })

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown)
  document.addEventListener('keydown', onDocumentKeyDown)
  window.addEventListener('resize', updatePosition)
  window.visualViewport?.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', onViewportScroll, true)
})

onBeforeUnmount(() => {
  clearTimers()
  document.removeEventListener('pointerdown', onDocumentPointerDown)
  document.removeEventListener('keydown', onDocumentKeyDown)
  window.removeEventListener('resize', updatePosition)
  window.visualViewport?.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onViewportScroll, true)
})
</script>

<template>
  <span class="retention-help">
    <button
      ref="trigger"
      class="retention-help__trigger"
      :class="{ 'is-active': visible }"
      type="button"
      :aria-label="$t('enterprise_ui_records_retention_title')"
      aria-haspopup="dialog"
      :aria-expanded="visible"
      :aria-pressed="pinned"
      :aria-controls="visible ? tooltipId : undefined"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
      @focus="open"
      @blur="scheduleClose"
      @click.stop="togglePinned"
    >
      <WorkbenchIcon name="info" :size="15" />
    </button>

    <Teleport to="body">
      <section
        v-if="visible"
        :id="tooltipId"
        ref="tooltip"
        class="retention-tooltip"
        :class="{ 'is-above': position.placement === 'above' }"
        :dir="rtl ? 'rtl' : 'ltr'"
        role="dialog"
        :aria-label="$t('enterprise_ui_records_retention_title')"
        :style="{
          top: `${position.top}px`,
          left: `${position.left}px`,
          visibility: ready ? 'visible' : 'hidden',
          '--retention-arrow-left': `${position.arrowLeft}px`,
          '--retention-max-height': `${position.maxHeight}px`,
        }"
        @mouseenter="onTooltipEnter"
        @mouseleave="onTooltipLeave"
        @wheel.stop
        @touchmove.stop
      >
        <div ref="tooltipContent" class="retention-tooltip__content">
          <strong class="retention-tooltip__title">{{ $t('enterprise_ui_records_retention_title') }}</strong>

          <div class="retention-tooltip__summary">
            <strong>{{ $t('enterprise_ui_records_retention_summary_title') }}</strong>
            <span>{{ $t('enterprise_ui_records_retention_summary_description') }}</span>
          </div>

          <div class="retention-tooltip__item">
            <strong>{{ $t('enterprise_ui_records_retention_plan_expiry_title') }}</strong>
            <span>{{ $t('enterprise_ui_records_retention_plan_expiry_description') }}</span>
          </div>

          <div class="retention-tooltip__item">
            <strong>{{ $t('enterprise_ui_records_retention_all_plans_title') }}</strong>
            <span>{{ $t('enterprise_ui_records_retention_all_plans_description') }}</span>
          </div>

          <span class="retention-tooltip__footer">{{ $t('enterprise_ui_records_retention_footer') }}</span>
        </div>
      </section>
    </Teleport>
  </span>
</template>

<style scoped>
.retention-help {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

.retention-help__trigger {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #e5484d;
  background: transparent;
  cursor: pointer;
}

.retention-help__trigger:hover,
.retention-help__trigger:focus-visible,
.retention-help__trigger.is-active {
  color: #c62f35;
  background: #fdecec;
  outline: none;
}

.retention-tooltip {
  position: fixed;
  z-index: 3300;
  width: 400px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  border: 1px solid #ebeef2;
  border-radius: 12px;
  color: #51565d;
  background: #fff;
  box-shadow: 0 10px 30px #0a234d2e;
  font-family: inherit;
}

.retention-tooltip__content {
  display: flex;
  max-height: var(--retention-max-height, calc(100vh - 24px));
  box-sizing: border-box;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 11px;
  scrollbar-width: thin;
  scrollbar-color: #cbd2dc transparent;
}

.retention-tooltip__content::-webkit-scrollbar { width: 6px; }
.retention-tooltip__content::-webkit-scrollbar-thumb { border-radius: 6px; background: #cbd2dc; }

.retention-tooltip::before,
.retention-tooltip::after {
  position: absolute;
  left: var(--retention-arrow-left);
  width: 0;
  height: 0;
  border-right: 7px solid transparent;
  border-left: 7px solid transparent;
  content: '';
}

.retention-tooltip::before {
  top: -7px;
  border-bottom: 7px solid #ebeef2;
}

.retention-tooltip::after {
  top: -6px;
  border-bottom: 7px solid #fff;
}

.retention-tooltip.is-above::before {
  top: auto;
  bottom: -7px;
  border-top: 7px solid #ebeef2;
  border-bottom: 0;
}

.retention-tooltip.is-above::after {
  top: auto;
  bottom: -6px;
  border-top: 7px solid #fff;
  border-bottom: 0;
}

.retention-tooltip__title {
  color: #1f2329;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
}

.retention-tooltip__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecec;
}

.retention-tooltip__summary strong {
  color: #c62f35;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.6;
}

.retention-tooltip__summary span {
  color: #7a2a2e;
  font-size: 11.5px;
  line-height: 1.6;
}

.retention-tooltip__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.retention-tooltip__item strong {
  color: #1f2329;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.6;
}

.retention-tooltip__item span {
  color: #51565d;
  font-size: 11.5px;
  line-height: 1.6;
}

.retention-tooltip__footer {
  color: #8f959e;
  font-size: 11px;
  line-height: 1.6;
}
</style>
